import type { QuoteFormError } from 'state/addonForm/addonForm.selector';
import { ExposedFormStatus, FormStatus } from 'state/forms/models';
import {
  getBatchTradeInfos,
  getFormIdsInBatchTab,
  getSelectedFormIdsInBatchTab,
  isAnyBatchFormMatchingStatus,
} from 'state/selectors';
import type { AppState } from 'state/state.model';

import {
  canBatchFormBeAbortedByStatus,
  canBatchFormBeClonedByStatus,
  canBatchFormBeDeletedByStatus,
  canBatchFormBeEditedByStatus,
  canBatchFormBeExportedByStatus,
  canBatchFormBeOpenedInQuoteWorkspaceByStatus,
  canBatchFormBePricedByStatus,
  canBatchFormDatesBeRefreshedByStatus,
  isBatchFormActionFailedByStatus,
  isBatchFormLoadingByStatus,
  isNbOfBatchFormWithinThreshold,
  translateFormStatus,
} from './infos.helpers';
import type { BatchFormInfos, BatchFormInfosStateMap } from './infos.model';

// TODO: batch screen unit test

export function selectBatchFormsState(state: AppState): BatchFormInfosStateMap {
  return state.batchForm.infos;
}

export function getBatchFormInfos(state: AppState, formId: string): BatchFormInfos | undefined {
  return state.batchForm.infos[formId];
}

export function getBatchFormIndex(state: AppState, formId: string): number {
  return Object.keys(state.batchForm.infos).findIndex(batchFormId => batchFormId === formId);
}

export function getBatchFormIdsByQuoteId(state: AppState, quoteId: string): readonly string[] {
  const formInfos = state.batchForm.infos;
  const formIds = [];

  for (const formId in formInfos) {
    const info = formInfos[formId];
    if (quoteId === info.quoteId) {
      formIds.push(formId);
    }
  }

  return formIds;
}

export function getBatchFormValidationErrors(state: AppState, formId: string): readonly QuoteFormError[] {
  const infos = getBatchFormInfos(state, formId);
  return infos?.validationErrors ?? [];
}

export function getBatchFormStatus(state: AppState, formId: string): FormStatus | undefined {
  const infos = getBatchFormInfos(state, formId);
  return infos ? infos.status : undefined;
}

export function getExposedBatchFormStatus(state: AppState, formId: string): ExposedFormStatus | undefined {
  const formStatus = getBatchFormStatus(state, formId);
  return formStatus ? translateFormStatus(formStatus) : undefined;
}

export function getFormIdsInBatchTabIsNotError(state: AppState, formIds: readonly string[]): readonly string[] {
  return formIds.filter((formId: string) => {
    const form = getBatchFormInfos(state, formId);
    return form && !isBatchFormActionFailedByStatus(form.status);
  });
}

export function areBatchFormsSelectedByFormIds(state: AppState, formIds: readonly string[]): boolean {
  formIds = formIds.filter((formId: string) => {
    const form = getBatchFormInfos(state, formId);
    return form && !isBatchFormActionFailedByStatus(form.status);
  });
  return formIds.every(formId => {
    const form = getBatchFormInfos(state, formId);
    return form?.isSelected;
  });
}

// TODO: Unit test
export function isNoFormSelected(state: AppState, formIds: readonly string[]): boolean {
  formIds = formIds.filter((formId: string) => {
    const form = getBatchFormInfos(state, formId);
    return form && !isBatchFormActionFailedByStatus(form.status);
  });
  return formIds.every(formId => {
    const form = getBatchFormInfos(state, formId);
    return !form?.isSelected;
  });
}

export function isBatchFormSelected(state: AppState, formId: string): boolean {
  const form = getBatchFormInfos(state, formId);
  return form ? form.isSelected : false;
}

export function isBatchFormLoading(state: AppState, formId: string) {
  const form = getBatchFormInfos(state, formId);
  return form ? isBatchFormLoadingByStatus(form.status) : false;
}

export function isBatchFormActionFailed(state: AppState, formId: string): boolean {
  const formInfo = getBatchFormInfos(state, formId);
  return formInfo ? isBatchFormActionFailedByStatus(formInfo.status) : false;
}

export function canExportBatchForm(appState: AppState, formId: string): boolean {
  const formInfo = getBatchFormInfos(appState, formId);
  return formInfo !== undefined ? canBatchFormBeExportedByStatus(formInfo.status) : false;
}

export function canDeleteBatchForm(appState: AppState, formId: string): boolean {
  const formInfo = getBatchFormInfos(appState, formId);
  return formInfo !== undefined ? canBatchFormBeDeletedByStatus(formInfo.status) : false;
}
export function canOpenInQuoteWorkspaceBatchForm(appState: AppState, formId: string): boolean {
  const formInfo = getBatchFormInfos(appState, formId);
  const tradeInfo = getBatchTradeInfos(appState, formId);
  const tradeAuthStatus = tradeInfo?.tradeAuthStatus;
  return formInfo !== undefined && tradeAuthStatus !== undefined
    ? canBatchFormBeOpenedInQuoteWorkspaceByStatus(formInfo.status)
    : false;
}

export function canPriceBatchForm(state: AppState, formId: string): boolean {
  const formInfo = getBatchFormInfos(state, formId);
  return formInfo !== undefined ? canBatchFormBePricedByStatus(formInfo.status) : false;
}

export function canEditBatchForm(state: AppState, formId: string): boolean {
  const formInfo = getBatchFormInfos(state, formId);
  return formInfo !== undefined ? canBatchFormBeEditedByStatus(formInfo.status) : false;
}

export function canOpenApplyBatchForm(state: AppState, tabId: string): boolean {
  return !isAnyBatchFormMatchingStatus(state, tabId, 'QUOTING') && hasSelectedBatchFormsForTab(state, tabId);
}

export function hasSelectedBatchFormsForTab(state: AppState, tabId: string): boolean {
  return getSelectedFormIdsCountByBatchTabId(state, tabId) > 0;
}
export function canDeleteSelectedFormsInBatchTab(appState: AppState, tabId: string): boolean {
  const formIds = getSelectedFormIdsInBatchTab(appState, tabId);
  const canDelete = formIds.every(formId => canDeleteBatchForm(appState, formId));
  return formIds.length > 0 && canDelete;
}

export function canOpenInQuoteWorkspaceSelectedFormsInBatchTab(appState: AppState, tabId: string): boolean {
  const formIds = getSelectedFormIdsInBatchTab(appState, tabId);
  const canOpen = formIds.every(formId => canOpenInQuoteWorkspaceBatchForm(appState, formId));
  return formIds.length > 0 && canOpen;
}

export function canExportSelectedFormsInBatchTab(appState: AppState, tabId: string): boolean {
  const formIds = getSelectedFormIdsInBatchTab(appState, tabId);
  const canExport = formIds.every(formId => canExportBatchForm(appState, formId));
  return formIds.length > 0 && canExport;
}

export function canPriceSelectedFormsInBatchTab(appState: AppState, tabId: string): boolean {
  const formIds = getSelectedFormIdsInBatchTab(appState, tabId);
  const canPrice = formIds.every(formId => canPriceBatchForm(appState, formId));
  return formIds.length > 0 && canPrice;
}

export function getBatchFormVersion(state: AppState, formId: string): number | undefined {
  const formInfo = getBatchFormInfos(state, formId);
  return formInfo?.version;
}

export function getBatchFormQuoteQuery(state: AppState, formId: string): unknown {
  const formInfo = getBatchFormInfos(state, formId);
  return formInfo?.quoteQuery;
}

export function getBatchFormProductType(state: AppState, formId: string): string | undefined {
  const formInfo = getBatchFormInfos(state, formId);
  return formInfo?.productType;
}

export function getBatchFormProductSubtype(state: AppState, formId: string): string | undefined {
  const formInfo = getBatchFormInfos(state, formId);
  return formInfo?.productSubType;
}

export function getBatchFormQuoteId(state: AppState, formId: string): string | undefined {
  const infos = getBatchFormInfos(state, formId);
  return infos ? infos.quoteId : undefined;
}
// TODO: Unit test
export function canCloneBatchForm(appState: AppState, formId: string): boolean {
  const formInfo = getBatchFormInfos(appState, formId);
  return formInfo !== undefined ? canBatchFormBeClonedByStatus(formInfo.status) : false;
}

// TODO: Unit test
export function canCloneSelectedFormsInBatchTab(appState: AppState, tabId: string): boolean {
  const formIds = getFormIdsInBatchTab(appState, tabId);
  const selectedFormIds = getSelectedFormIdsInBatchTab(appState, tabId);
  const nbFormsWithinThreshold = isNbOfBatchFormWithinThreshold(formIds.length + selectedFormIds.length);
  const canClone = selectedFormIds.every(formId => canCloneBatchForm(appState, formId));
  return selectedFormIds.length > 0 && nbFormsWithinThreshold && canClone;
}

// TODO: Unit test
export function canRefreshBatchFormDate(appState: AppState, formId: string): boolean {
  const formInfo = getBatchFormInfos(appState, formId);
  return formInfo !== undefined ? canBatchFormDatesBeRefreshedByStatus(formInfo.status) : false;
}

// TODO: Unit test
export function canRefreshSelectedFormsDatesInBatchTab(appState: AppState, tabId: string): boolean {
  const selectedFormIds = getSelectedFormIdsInBatchTab(appState, tabId);
  if (selectedFormIds.length <= 0) {
    return false;
  }

  return selectedFormIds.every(formId => canRefreshBatchFormDate(appState, formId));
}

// TODO: Unit test
export function getBatchFormJsonRepresentation(appState: AppState, formId: string): string | undefined {
  const formInfos = getBatchFormInfos(appState, formId);
  return formInfos !== undefined ? JSON.stringify(formInfos, undefined, 2) : undefined;
}

export function isTradeablePricingBlocked(state: AppState, formId: string): boolean {
  const formInfo = getBatchFormInfos(state, formId);
  if (!formInfo) {
    return true;
  }

  const tradableQuote = formInfo.actions.find(action => action.name === 'TradableQuote');
  if (!tradableQuote) {
    return true;
  }

  return tradableQuote.isDisabled ?? false;
}

// TODO: Unit test
export const canAbortBatchForm = (appState: AppState, formId: string): boolean => {
  const formInfos = getBatchFormInfos(appState, formId);
  return formInfos !== undefined
    ? canBatchFormBeAbortedByStatus(formInfos.status) && formInfos.quoteId !== undefined
    : false;
};

// TODO: Unit test
export function canAbortSelectedFormsInBatchTab(appState: AppState, tabId: string): boolean {
  const selectedFormIds = getSelectedFormIdsInBatchTab(appState, tabId);
  if (selectedFormIds.length <= 0) {
    return false;
  }
  return selectedFormIds.every(formId => canAbortBatchForm(appState, formId));
}

export function getSelectedFormIdsCountByBatchTabId(appState: AppState, tabId: string): number {
  return getSelectedFormIdsInBatchTab(appState, tabId)?.length ?? 0;
}
